import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { renderTemplate, type TemplateSummary } from "../lib/templates";
import { Button } from "./ui/button";

/** Email bodies render at 600px; the thumbnail scales that to the card width. */
const EMAIL_WIDTH = 600;

function formatUpdatedAt(raw: unknown): string {
  if (typeof raw !== "string" || raw === "") return "—";
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(date);
}

/**
 * One "Your templates" card — a lazy, scaled, sandboxed-iframe preview of the
 * live template HTML (STOP-1 option A: no stored-thumbnail infra, always
 * fresh). The render call fires only once the card scrolls into view
 * (IntersectionObserver), and the 600px email is scaled to the measured card
 * width (ResizeObserver). Hover/focus reveals Preview / Edit / Duplicate /
 * Delete — all keyboard-reachable (the action bar uses focus-within, the card
 * itself is a button to the editor).
 */
export function TemplatePreviewCard({
  template,
  onPreview,
  onDuplicate,
  onDelete,
  duplicating,
  deleting,
}: {
  template: TemplateSummary;
  onPreview: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  duplicating: boolean;
  deleting: boolean;
}): JSX.Element {
  const navigate = useNavigate();
  const previewRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [scale, setScale] = useState(0.36);

  // Lazy: only render the (potentially heavy) iframe once the card is near
  // the viewport — a grid of 25 templates must not fire 25 renders at once.
  useEffect(() => {
    const node = previewRef.current;
    if (node === null || visible) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) setVisible(true);
      },
      { rootMargin: "200px" },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [visible]);

  // Scale the 600px email down to the actual card width.
  useEffect(() => {
    const node = previewRef.current;
    if (node === null) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 0;
      if (w > 0) setScale(w / EMAIL_WIDTH);
    });
    ro.observe(node);
    return () => ro.disconnect();
  }, []);

  const { data: html, isLoading, isError } = useQuery({
    queryKey: ["template", "render", template.id, template.updated_at],
    queryFn: () => renderTemplate(template.id),
    enabled: visible,
    staleTime: 60_000,
    retry: 1,
  });

  const editHref = `/templates/${template.id}/edit`;

  return (
    <div className="group relative flex flex-col overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-sm transition-shadow focus-within:ring-2 focus-within:ring-primary-500 hover:shadow-md">
      {/* Preview — click opens the editor. role=button + key handler for a11y. */}
      <div
        ref={previewRef}
        role="button"
        tabIndex={0}
        aria-label={`Open ${template.name} in the editor`}
        onClick={() => navigate(editHref)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            navigate(editHref);
          }
        }}
        className="relative aspect-[3/4] w-full cursor-pointer overflow-hidden border-b border-neutral-100 bg-neutral-50"
      >
        {!visible || isLoading ? (
          <div className="h-full w-full animate-pulse bg-neutral-100" aria-hidden="true" />
        ) : isError || html === undefined || html === "" ? (
          <div className="flex h-full w-full items-center justify-center px-3 text-center text-xs text-neutral-400">
            Preview unavailable
          </div>
        ) : (
          <iframe
            title={`Preview of ${template.name}`}
            srcDoc={html}
            sandbox=""
            scrolling="no"
            tabIndex={-1}
            aria-hidden="true"
            style={{
              width: EMAIL_WIDTH,
              height: EMAIL_WIDTH / scale,
              transform: `scale(${scale})`,
              transformOrigin: "top left",
              border: 0,
              pointerEvents: "none",
            }}
          />
        )}
      </div>

      {/* Meta */}
      <div className="flex min-w-0 flex-col gap-0.5 px-3 py-2">
        <p className="truncate text-sm font-medium text-neutral-900">{template.name}</p>
        <p className="text-xs text-neutral-500">Updated {formatUpdatedAt(template.updated_at)}</p>
      </div>

      {/* Quick actions — revealed on hover/focus, always keyboard-reachable. */}
      <div className="pointer-events-none absolute inset-x-0 bottom-12 flex justify-center gap-1.5 opacity-0 transition-opacity group-focus-within:pointer-events-auto group-focus-within:opacity-100 group-hover:pointer-events-auto group-hover:opacity-100">
        <Button variant="outline" size="sm" onClick={onPreview} aria-label={`Preview ${template.name}`}>
          Preview
        </Button>
        <Button variant="primary" size="sm" onClick={() => navigate(editHref)} aria-label={`Edit ${template.name}`}>
          Edit
        </Button>
        <Button
          variant="outline"
          size="sm"
          loading={duplicating}
          onClick={onDuplicate}
          aria-label={`Duplicate ${template.name}`}
        >
          Duplicate
        </Button>
        <Button
          variant="destructive"
          size="sm"
          loading={deleting}
          aria-label={`Delete ${template.name}`}
          onClick={() => {
            if (window.confirm(`Delete "${template.name}"? This soft-deletes the template.`)) {
              onDelete();
            }
          }}
        >
          Delete
        </Button>
      </div>
    </div>
  );
}
